import { useState, useEffect } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { supabase } from '../../lib/supabase'
import Logo from '../../components/Logo'
import { Eye, EyeOff, AlertCircle, CheckCircle, Car, Users, Bell, Wrench } from 'lucide-react'

export default function SignUp() {
  const navigate = useNavigate()
  const { token } = useParams() // Legacy invite token support
  const [searchParams] = useSearchParams()
  const { signUp, updatePassword } = useAuth()

  // Check for Supabase auth tokens in URL (from email invite)
  const accessToken = searchParams.get('access_token') || window.location.hash?.match(/access_token=([^&]*)/)?.[1]
  const refreshToken = searchParams.get('refresh_token') || window.location.hash?.match(/refresh_token=([^&]*)/)?.[1]

  // For Supabase invite flow (setting password after email invite)
  const [isSettingPassword, setIsSettingPassword] = useState(false)
  const [userEmail, setUserEmail] = useState('')

  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  // Handle Supabase auth callback (from email invite - legacy support)
  useEffect(() => {
    const handleAuthCallback = async () => {
      if (accessToken && refreshToken) {
        try {
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          })

          if (error) {
            console.error('Session error:', error)
            setError('Invalid or expired link. Please request a new invitation.')
            return
          }

          if (data.user) {
            setUserEmail(data.user.email)
            setIsSettingPassword(true)
            window.history.replaceState({}, document.title, window.location.pathname)
          }
        } catch (err) {
          console.error('Auth callback error:', err)
          setError('Something went wrong. Please try again.')
        }
      }
    }

    handleAuthCallback()
  }, [accessToken, refreshToken])

  const validatePassword = () => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters'
    }
    if (password !== confirmPassword) {
      return 'Passwords do not match'
    }
    return null
  }

  // Handle setting password for Supabase invite
  const handleSetPassword = async (e) => {
    e.preventDefault()
    setError('')

    const passwordError = validatePassword()
    if (passwordError) {
      setError(passwordError)
      return
    }

    setLoading(true)

    try {
      const { error } = await updatePassword(password)

      if (error) {
        setError(error.message)
        return
      }

      setSuccess(true)
      setTimeout(() => {
        navigate('/garage')
      }, 2000)
    } catch (err) {
      setError('An unexpected error occurred')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  // Handle open registration
  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    if (!fullName.trim()) {
      setError('Please enter your name')
      return
    }

    const passwordError = validatePassword()
    if (passwordError) {
      setError(passwordError)
      return
    }

    setLoading(true)

    try {
      const { error } = await signUp(email, password, fullName)

      if (error) {
        if (error.message.includes('already registered')) {
          setError('This email is already registered. Try signing in instead.')
        } else {
          setError(error.message)
        }
        return
      }

      setSuccess(true)
    } catch (err) {
      setError('An unexpected error occurred')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-rdc-cream flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-sm p-8 border border-rdc-cream max-w-md w-full text-center">
          <Logo className="mb-6" />
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={32} className="text-green-500" />
          </div>
          <h1 className="font-display text-xl font-semibold text-black mb-2">
            {isSettingPassword ? 'Password Set!' : 'Check Your Email'}
          </h1>
          <p className="text-rdc-taupe mb-6">
            {isSettingPassword
              ? "Your password has been set. Redirecting you to your garage..."
              : "We've sent you a confirmation link. Click it to activate your account and start building your garage."}
          </p>
          {!isSettingPassword && (
            <Link to="/login" className="btn-primary inline-block">
              Go to Login
            </Link>
          )}
        </div>
      </div>
    )
  }

  // Setting password from Supabase email invite (legacy support)
  if (isSettingPassword) {
    return (
      <div className="min-h-screen bg-rdc-cream flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-sm p-8 border border-rdc-cream">
            <Logo className="mb-6" />

            <h1 className="font-display text-2xl font-semibold text-black mb-2">
              Set Your Password
            </h1>
            <p className="text-rdc-taupe mb-8">
              Welcome! Create a password for <span className="font-medium text-rdc-dark-gray">{userEmail}</span>
            </p>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3 text-red-700">
                <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <form onSubmit={handleSetPassword} className="space-y-5">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-rdc-dark-gray mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-field pr-12"
                    placeholder="••••••••"
                    required
                    minLength={8}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-rdc-taupe hover:text-rdc-dark-gray"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                <p className="mt-1 text-xs text-rdc-taupe">
                  Must be at least 8 characters
                </p>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-rdc-dark-gray mb-2">
                  Confirm Password
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input-field"
                  placeholder="••••••••"
                  required
                  disabled={loading}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full py-3"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Setting password...
                  </span>
                ) : (
                  'Set Password & Continue'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    )
  }

  // Open registration form
  return (
    <div className="min-h-screen bg-rdc-cream flex">
      {/* Left Panel - Features */}
      <div className="hidden lg:flex lg:w-1/2 bg-white items-center justify-center p-12">
        <div className="max-w-md">
          <Logo size="large" className="mb-8" />
          <h2 className="font-display text-2xl text-black mb-4">
            Your Digital Garage Awaits
          </h2>
          <p className="text-rdc-taupe leading-relaxed mb-8">
            Join thousands of car enthusiasts managing their collections,
            tracking maintenance, and connecting with fellow collectors.
          </p>

          {/* Feature highlights */}
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-rdc-primary/10 flex items-center justify-center flex-shrink-0">
                <Car size={20} className="text-rdc-primary" />
              </div>
              <div>
                <div className="font-medium text-black">Showcase Your Collection</div>
                <div className="text-sm text-rdc-taupe">Beautiful profiles to display your vehicles</div>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-rdc-primary/10 flex items-center justify-center flex-shrink-0">
                <Wrench size={20} className="text-rdc-primary" />
              </div>
              <div>
                <div className="font-medium text-black">Smart Maintenance Tracking</div>
                <div className="text-sm text-rdc-taupe">AI-powered alerts for upcoming service needs</div>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-rdc-primary/10 flex items-center justify-center flex-shrink-0">
                <Users size={20} className="text-rdc-primary" />
              </div>
              <div>
                <div className="font-medium text-black">Connect with Collectors</div>
                <div className="text-sm text-rdc-taupe">Follow garages, discover cars, share your passion</div>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-rdc-primary/10 flex items-center justify-center flex-shrink-0">
                <Bell size={20} className="text-rdc-primary" />
              </div>
              <div>
                <div className="font-medium text-black">Never Miss a Deadline</div>
                <div className="text-sm text-rdc-taupe">Registration, insurance & service reminders</div>
              </div>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-rdc-warm-gray/30">
            <p className="text-sm text-rdc-taupe">
              <span className="font-medium text-rdc-forest">100% Free</span> — No credit card required
            </p>
          </div>
        </div>
      </div>

      {/* Right Panel - Sign Up Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden mb-8">
            <Logo className="mb-4" />
          </div>

          <div className="bg-white rounded-2xl shadow-sm p-8 border border-rdc-cream">
            <h1 className="font-display text-2xl font-semibold text-black mb-2">
              Create your garage
            </h1>
            <p className="text-rdc-taupe mb-8">
              Start tracking your collection in minutes
            </p>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3 text-red-700">
                <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="fullName" className="block text-sm font-medium text-rdc-dark-gray mb-2">
                  Your Name
                </label>
                <input
                  id="fullName"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="input-field"
                  placeholder="John Smith"
                  required
                  disabled={loading}
                  autoFocus
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-rdc-dark-gray mb-2">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-field"
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                  disabled={loading}
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-rdc-dark-gray mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-field pr-12"
                    placeholder="••••••••"
                    required
                    minLength={8}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-rdc-taupe hover:text-rdc-dark-gray"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                <p className="mt-1 text-xs text-rdc-taupe">
                  Must be at least 8 characters
                </p>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-rdc-dark-gray mb-2">
                  Confirm Password
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input-field"
                  placeholder="••••••••"
                  required
                  disabled={loading}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full py-3"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Creating your garage...
                  </span>
                ) : (
                  'Create Free Account'
                )}
              </button>
            </form>

            <p className="mt-6 text-center text-sm text-rdc-taupe">
              Already have an account?{' '}
              <Link to="/login" className="text-rdc-primary hover:underline font-medium">
                Sign in
              </Link>
            </p>

            <p className="mt-4 text-center text-xs text-rdc-taupe">
              By creating an account, you agree to our{' '}
              <Link to="/terms" className="underline hover:text-rdc-charcoal">Terms of Service</Link>
              {' '}and{' '}
              <Link to="/privacy" className="underline hover:text-rdc-charcoal">Privacy Policy</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
